import React, { Fragment, ReactNode, useState } from 'react'
import { isClozeDropdown, isDropdownAnswer, Image } from '../../utils'
import { EMPTY_INITIAL_ANSWER } from '../../utils/constants'
import { ClozeDropdownProps } from './ClozeDropdown'
import Footer from '../../components/studentPromptFooter'
import { StudentQuestionProps } from '../Question'
import OptionsSection from '../../components/options-wrapper'
import {
	DropdownAnswer,
	parseTemplateStringIntoSegments,
	QuestionChoice,
	StudentAnswer,
	TemplateSegmentType,
	variantTypes,
} from '@mission.io/question-toolkit'
import DropdownReviewResponse from '../../components/dropdown-review-response/DropdownReviewResponse'
import {
	ClassResponse,
	DropdownClozeDropdown,
	QuestionVariant,
} from '../../model/general'
import Popover from '../../components/popover'
import { LabelInformation, GeneralScoreTitle } from '../../assets/styles'
import { AiOutlineCheck, AiOutlineClose } from 'react-icons/ai'
import 'styled-components/macro'
import { styled } from 'styled-components'
import { ListOptionsStats } from '../image-labeling/Student'
import FooterStickyContainer from '../../components/footer-sticky-container/FooterStickyContainer'
import {
	ClozeDropdownProvider,
	getTemplateMarkdownString,
	TEXT_TO_SPEECH_CLOSE_READING_MARKDOWN_OVERRIDES,
} from './helpers'
import OptionalMarkdown from '../../components/Markdown/OptionalMarkdown'
import { isMarkdownString } from '../../components/Markdown/hooks'
import { _tempTheme } from '../../utils/tempTheme'

const initialValue = EMPTY_INITIAL_ANSWER

/**
 * A component for a student view. It has a prompt and reviewed view.
 * @param {x: ClozeDropdownProps} props
 * @returns {JSX.Element}
 */
export const StudentClozeDropdown = (
	props: ClozeDropdownProps
): JSX.Element => {
	const {
		question,
		textToSpeech: TextToSpeech,
		requestId,
		review,
		sendMessage,
		studentResponse,
		questionIndex,
		parentRef,
		classResponse,
	} = props

	if (!question || !isClozeDropdown(question)) return <Fragment />

	const [error, setError] = useState('')
	const [templateKeySelected, setTemplateKeySelected] = useState(
		// @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
		question.dropdowns.length ? question.dropdowns[0].templateResponseKey : ''
	)
	const [localStudentAnswer, setLocalStudentAnswer] = useState<DropdownAnswer>({
		type: QuestionVariant.CLOZE_DROPDOWN,
		value: {},
	})

	const handleChangeSelectedDropdown = (templateKey: string) => {
		setTemplateKeySelected(templateKey)
	}

	const handleChangeOption = (index: number) => {
		const choice = question.dropdowns.find(
			item => item.templateResponseKey === templateKeySelected
		)?.choices[index]?._id

		setLocalStudentAnswer(state => ({
			...state,
			value: {
				...state.value,
				[templateKeySelected]: String(choice || ''),
			},
		}))
		setError('')
	}

	const handleSubmit = () => {
		if (
			Object.keys(localStudentAnswer.value).length !== question.dropdowns.length
		) {
			setError('Please Complete All Options')
			return
		}

		setError('')
		sendMessage && sendMessage(localStudentAnswer)
	}
	const instantReview = review === 'INSTANT'
	if (question) {
		if (instantReview || (!review && requestId)) {
			const dropdown = question.dropdowns.find(
				item => item.templateResponseKey === templateKeySelected
			)
			const optionSection = (
				<OptionsSection
					options={dropdown?.choices}
					studentResponse={studentResponse}
					dropdownKey={templateKeySelected}
					handleClick={handleChangeOption}
					isReview={instantReview}
				/>
			)

			const prompt = 'Please select the correct options from the phrase below'
			let studentAnswer: DropdownAnswer | undefined = localStudentAnswer
			if (instantReview) {
				if (!studentResponse || !isDropdownAnswer(studentResponse.answer)) {
					studentAnswer = undefined
				} else {
					studentAnswer = studentResponse?.answer
				}
			}

			const classResponseScores =
				classResponse &&
				classResponse.scores.type === variantTypes.CLOZE_DROPDOWN &&
				classResponse.scores.optionScores.find(
					i => i.templateResponsekey === templateKeySelected
				)
			return (
				<FooterStickyContainer
					instantReview={instantReview}
					footerContent={
						<>
							{classResponse ? (
								classResponseScores &&
								dropdown && (
									<ListOptionsStats
										classResponse={classResponse}
										dropdown={dropdown}
										classResponseScores={classResponseScores}
										textToSpeech={TextToSpeech}
										review={review}
									/>
								)
							) : (
								<Footer
									studentAnswer={studentAnswer}
									isReview={instantReview}
									question={question}
									error={error}
									onClickSubmit={handleSubmit}
								/>
							)}
						</>
					}
					topContent={
						<>
							{classResponse ? (
								<GeneralScoreTitle>
									<div className="question-index">{`${props.questionIndex}.`}</div>
									{`${(
										classResponse.generalScore * 100
									).toFixed()}% Answered Correctly`}
								</GeneralScoreTitle>
							) : (
								<LabelInformation>
									{TextToSpeech ? (
										<TextToSpeech>{prompt}</TextToSpeech>
									) : (
										<span>{prompt}</span>
									)}
								</LabelInformation>
							)}
							<Image media={question.media} expandable parentRef={parentRef} />

							<TemplatePhrase>
								{buildTemplatePhrase({
									template: question.template,
									onClick: handleChangeSelectedDropdown,
									studentAnswer: studentAnswer as StudentAnswer,
									dropdowns: question.dropdowns,
									isReview: instantReview,
									TextToSpeech,
									optionSection,
									parentRef,
									classResponse,
									templateKeySelected,
								})}
							</TemplatePhrase>
						</>
					}
				/>
			)
		} else if (review === 'OVERVIEW') {
			const optionSection = (
				<OptionsSection
					options={
						question.dropdowns.find(
							item => item.templateResponseKey === templateKeySelected
						)?.choices
					}
					studentResponse={studentResponse}
					dropdownKey={templateKeySelected}
					handleClick={handleChangeOption}
					isReview={true}
				/>
			)

			return (
				<DropdownReviewResponse
					title="Please select the correct options from the phrase below"
					studentAnswer={studentResponse?.answer}
					index={questionIndex || 0}
					question={question}>
					<Wrapper>
						<TemplatePhrase>
							{buildTemplatePhrase({
								template: question.template,
								onClick: handleChangeSelectedDropdown,
								studentAnswer: studentResponse?.answer,
								dropdowns: question.dropdowns,
								isReview: true,
								TextToSpeech,
								optionSection,
								parentRef,
							})}
						</TemplatePhrase>
					</Wrapper>
				</DropdownReviewResponse>
			)
		}
	}

	return <Fragment />
}

const Wrapper = styled.div`
	background-color: ${({ theme }) => theme.primaryColor};
	box-shadow: 0px ${_tempTheme.spacing1} ${_tempTheme.spacing1}
		rgba(0, 0, 0, 0.25);
	border-radius: ${_tempTheme.spacing2};
	margin-top: ${_tempTheme.spacing4};
	padding: ${_tempTheme.spacing4};
`

/**
 * Functions that takes a template text for cloze dropdown and convert
 * in a component with buttons.
 *
 * @param {string} template - the is the original template from the question without any transformations
 * @param {(keyChoice: string) => void} onClick
 * @param {StudentAnswer} studentAnswer
 * @param {DropdownClozeDropdown[]} dropdowns
 * @param {StudentQuestionProps['textToSpeech']} textToSpeech
 * @param { React.ReactNode} optionSection
 * @param {boolean} isQuestionSlot
 * @param renderDropdownButton If provided, this function will be used to render the dropdown button(s)
 * @return {JSX.Element}
 */
export const buildTemplatePhrase = ({
	template,
	onClick,
	studentAnswer,
	dropdowns,
	isReview,
	TextToSpeech,
	optionSection,
	isQuestionSlot,
	renderDropdownButton,
	parentRef,
	classResponse,
	templateKeySelected,
}: {
	template: string
	onClick?: (keyChoice: string) => void
	studentAnswer?: StudentAnswer
	dropdowns?: DropdownClozeDropdown[]
	isReview?: boolean
	TextToSpeech?: StudentQuestionProps['textToSpeech']
	optionSection?: React.ReactNode
	isQuestionSlot?: boolean
	renderDropdownButton?: (arg: { dropdownId: string }) => ReactNode
	parentRef?: React.MutableRefObject<HTMLElement | undefined>
	classResponse?: ClassResponse | null
	templateKeySelected?: string
}): ReactNode => {
	if (studentAnswer && !isDropdownAnswer(studentAnswer)) {
		return null
	}

	const dropdownChoiceLookup: {
		[dropdownId: string]: { [choiceId: string]: QuestionChoice }
	} = {}
	if (dropdowns) {
		dropdowns.forEach(dropdown => {
			const choices: { [choiceId: string]: QuestionChoice } = {}
			dropdown.choices.forEach(choice => {
				if (choice._id) {
					choices[String(choice._id)] = choice
				}
			})
			dropdownChoiceLookup[dropdown.templateResponseKey] = choices
		})
	}

	function getDropdownButton({
		dropdownId,
	}: {
		dropdownId: string
	}): ReactNode {
		if (renderDropdownButton) {
			return renderDropdownButton({ dropdownId })
		}
		let choiceSelected = undefined
		if (dropdowns) {
			const studentsSelectedChoiceId =
				studentAnswer && isDropdownAnswer(studentAnswer)
					? studentAnswer.value[dropdownId]
					: null
			if (studentsSelectedChoiceId != null) {
				choiceSelected =
					dropdownChoiceLookup[dropdownId]?.[studentsSelectedChoiceId]
			}
		}

		if (isReview) {
			const options = dropdowns?.find(
				item => item.templateResponseKey === dropdownId
			)?.choices
			const correctOption = options?.find(i => i.answer)

			if (classResponse) {
				const score =
					classResponse.scores.type === variantTypes.CLOZE_DROPDOWN &&
					classResponse.scores.optionScores
						.find(i => i.templateResponsekey === dropdownId)
						?.choices.find(choice => choice._id === String(correctOption?._id))

				return (
					<ButtonNoStyle>
						<ButtonReview
							onClick={() => onClick && onClick(dropdownId)}
							role="option"
							$isCorrect={true}
							$isClassResponse={true}
							$isSelected={dropdownId === templateKeySelected}>
							<small>{`${score ? (score.score * 100).toFixed() : 0}%`}</small>
							<div className="text">
								<div css="display: flex;">
									{!choiceSelected?.answer && (
										<div className="class-response-text">
											{correctOption ? (
												<OptionalMarkdown>
													{correctOption.text}
												</OptionalMarkdown>
											) : (
												''
											)}
										</div>
									)}
								</div>
							</div>
						</ButtonReview>
					</ButtonNoStyle>
				)
			} else {
				return (
					<Popover
						handleClickTrigger={() => onClick && onClick(dropdownId)}
						parentElement={parentRef?.current}
						key={dropdownId}
						trigger={
							<ButtonReview
								role="option"
								$isCorrect={Boolean(choiceSelected?.answer)}>
								{choiceSelected?.answer ? (
									<AiOutlineCheck className="icon" aria-label="Correct Icon" />
								) : (
									<AiOutlineClose
										className="icon"
										aria-label="Incorrect Icon"
									/>
								)}
								<div className="text">
									<div css="display: flex;">
										<div
											className={
												studentAnswer
													? choiceSelected?.answer
														? 'answer'
														: 'incorrect-text'
													: 'no-answer'
											}>
											<OptionalMarkdown>
												{choiceSelected ? choiceSelected.text : initialValue}
											</OptionalMarkdown>
										</div>
										{!choiceSelected?.answer && (
											<div className="correct-text">
												{correctOption ? (
													<OptionalMarkdown>
														{correctOption.text}
													</OptionalMarkdown>
												) : (
													''
												)}
											</div>
										)}
									</div>
								</div>
							</ButtonReview>
						}
						triggerStyle={{ display: 'inline-block' }}>
						{optionSection}
					</Popover>
				)
			}
		}
		if (isQuestionSlot) {
			return (
				<ButtonPrompt style={{ cursor: 'default' }} $isAnswered={false}>
					{dropdownId}
				</ButtonPrompt>
			)
		}
		return (
			<Popover
				handleClickTrigger={() => onClick && onClick(dropdownId)}
				parentElement={parentRef?.current}
				key={dropdownId}
				trigger={
					<ButtonPrompt $isAnswered={choiceSelected ? true : false}>
						<OptionalMarkdown>
							{choiceSelected ? choiceSelected.text : initialValue}
						</OptionalMarkdown>
					</ButtonPrompt>
				}
				triggerStyle={{ display: 'inline-block' }}>
				{optionSection}
			</Popover>
		)
	}

	if (isMarkdownString(template)) {
		const dropdownButtonLookup: Record<string, ReactNode> = {}
		parseTemplateStringIntoSegments(template).forEach(segment => {
			if (segment.type === TemplateSegmentType.Input) {
				dropdownButtonLookup[segment.inputId] = getDropdownButton({
					dropdownId: segment.inputId,
				})
			}
		})
		return (
			<ClozeDropdownProvider
				state={{
					dropDowns: dropdownButtonLookup,
				}}>
				<OptionalMarkdown
					className="prose-on-game-light-purple"
					textToSpeech={TextToSpeech}
					interpretAsMarkdown={true}
					overrides={TEXT_TO_SPEECH_CLOSE_READING_MARKDOWN_OVERRIDES}>
					{getTemplateMarkdownString(template) ?? template}
				</OptionalMarkdown>
			</ClozeDropdownProvider>
		)
	}

	return parseTemplateStringIntoSegments(template).map((segment, index) => {
		const segmentKey = `segment-${index}`
		if (segment.type === TemplateSegmentType.Text) {
			const text = segment.text
			return (
				<Fragment key={segmentKey}>
					{TextToSpeech ? <TextToSpeech>{text}</TextToSpeech> : <>{text}</>}
				</Fragment>
			)
		}
		if (segment.type === TemplateSegmentType.Input) {
			return (
				<Fragment key={segmentKey}>
					{getDropdownButton({ dropdownId: segment.inputId })}
				</Fragment>
			)
		}
		return <Fragment key={segmentKey} />
	})
}
interface IButton {
	$isCorrect?: boolean
	$isSelected?: boolean
	$isClassResponse?: boolean
}

const ButtonNoStyle = styled.button`
	border: none;
	padding: 0px;
	margin: 0px;
	background: transparent;
	width: fit-content;
`

const ButtonReview = styled.div<IButton>`
	line-height: 20px;
	cursor: pointer;
	margin: ${_tempTheme.spacing1};
	font-weight: ${_tempTheme.font_semibold};
	font-size: ${_tempTheme.text_base};
	color: ${({ theme }) => theme.white};
	padding: ${({ $isClassResponse }) =>
		$isClassResponse ? '0' : `0 ${_tempTheme.spacing2}`};

	background: ${({ $isCorrect, theme }) =>
		$isCorrect ? theme.success : theme.error} !important;
	border: none;
	box-sizing: border-box;
	border-radius: ${_tempTheme.spacing1};

	border: ${({ theme, $isSelected }) =>
		$isSelected && `${_tempTheme.spacing1} solid ${theme.lightSuccess}`};

	.icon {
		margin-right: ${_tempTheme.spacing1};
		vertical-align: middle;
		display: inline-block;
		padding: 2px;
	}
	div.text {
		border-left: 2px solid ${({ theme }) => theme.white};
		display: inline-block;
	}
	.answer {
		padding: 2px ${_tempTheme.spacing2};
	}
	.no-answer {
		background: ${({ theme }) => theme.error};
		padding: 2px ${_tempTheme.spacing2};
	}
	.incorrect-text {
		background: ${({ theme }) => theme.error};
		padding: 2px ${_tempTheme.spacing2};
		text-decoration: line-through;
	}
	.correct-text {
		padding: 0 ${_tempTheme.spacing2};
		margin-right: -${_tempTheme.spacing2};
		background: ${({ theme }) => theme.success};
		border-radius: 0 ${_tempTheme.spacing1} ${_tempTheme.spacing1} 0;
		padding: 2px ${_tempTheme.spacing2};
	}
	.class-response-text {
		padding: 0 ${_tempTheme.spacing2};
		background: #05aba7;
		border-radius: 0 ${_tempTheme.spacing1} ${_tempTheme.spacing1} 0;
	}
	small {
		padding: 0 ${_tempTheme.spacing1};
	}
`
interface IButtonPrompt {
	$isAnswered?: boolean
}
const ButtonPrompt = styled.div<IButtonPrompt>`
	line-height: 1em;
	margin: ${_tempTheme.spacing1};
	font-weight: ${_tempTheme.font_semibold};
	font-size: ${_tempTheme.text_lg};
	color: ${({ theme }) => theme.white};
	padding: 2px ${_tempTheme.spacing2};
	background: #05aba7;
	box-sizing: border-box;
	display: inline-block;
	border-radius: ${_tempTheme.spacing1};
	cursor: pointer;
	border: ${({ $isAnswered, theme }) =>
		$isAnswered
			? `4px solid ${theme.secondaryColor}`
			: `2px solid ${theme.white}`};
`

export const TemplatePhrase = styled.div`
	font-family: ${({ theme }) => theme.fontFamily};
	font-style: normal;
	font-weight: ${_tempTheme.font_semibold};
	font-size: ${_tempTheme.text_lg};
	text-align: start;
	border-radius: ${_tempTheme.spacing1};
	color: ${({ theme }) => theme.white};
	margin: ${_tempTheme.spacing4} auto;

	span {
		line-height: ${_tempTheme.text_4xl};
		color: ${({ theme }) => theme.white};
	}
`
export default StudentClozeDropdown
